// src/storage/telemetryCollector.ts — Telemetry Collector & Impulsive Guess Detection (FR-DASH-003, FR-DASH-004)
// Records per-question response times, flags impulsive guesses (<1500ms),
// buckets cognitive fatigue into 10-minute segments, and exports full JSON archives for parents.

export const IMPULSIVE_THRESHOLD_MS = 1500; // <1.5s implies rapid unread guessing (FR-DASH-003)

export interface TelemetryEvent {
  id: string;
  questionId: string;
  domain: string;
  topic: string;
  durationMs: number;
  correct: boolean;
  timestamp: number;
  sessionElapsedSeconds: number; // Second within session (0..1800)
  isImpulsive: boolean;
}

export interface FatigueSegment {
  label: string; // "Mins 1–10", "Mins 11–20", "Mins 21–30"
  minMinute: number;
  maxMinute: number;
  totalAttempts: number;
  correctCount: number;
  accuracyPercent: number;
  impulsiveCount: number;
  impulsivePercent: number;
  averageDurationMs: number;
}

export interface CognitiveFatigueAnalysis {
  segments: [FatigueSegment, FatigueSegment, FatigueSegment];
  totalEvents: number;
  totalImpulsive: number;
  overallImpulsivePercent: number;
  fatigueOnsetDetected: boolean;
  accuracyDropPercent: number;
  recommendedMaxMinutes: number;
  parentInsight: string;
}

export interface TelemetryStorageData {
  version: number;
  events: TelemetryEvent[];
}

const STORAGE_KEY = 'clawey_telemetry_v1';

/** Helper: Determine if duration is an impulsive guess */
export function isImpulsiveGuess(durationMs: number): boolean {
  return durationMs < IMPULSIVE_THRESHOLD_MS;
}

/** Bucket seconds into segment index 0 (1-10), 1 (11-20), or 2 (21-30) */
export function getSegmentIndexForSeconds(elapsedSeconds: number): number {
  if (elapsedSeconds <= 600) return 0;
  if (elapsedSeconds <= 1200) return 1;
  return 2;
}

export class TelemetryCollector {
  /** Load all recorded telemetry events */
  static load(): TelemetryStorageData {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { version: 1, events: [] };
      const parsed = JSON.parse(raw) as Partial<TelemetryStorageData>;
      return {
        version: 1,
        events: Array.isArray(parsed.events) ? parsed.events : [],
      };
    } catch {
      return { version: 1, events: [] };
    }
  }

  /** Save telemetry events to storage */
  static save(data: TelemetryStorageData): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (err) {
      console.warn('Failed to save telemetry data:', err);
    }
  }

  /** Clear all telemetry data */
  static clear(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignored
    }
  }

  /** Record a single question response event (FR-DASH-003) */
  static recordEvent(params: {
    questionId: string;
    domain: string;
    topic: string;
    durationMs: number;
    correct: boolean;
    sessionElapsedSeconds: number;
    timestamp?: number;
  }): TelemetryEvent {
    const isImpulsive = isImpulsiveGuess(params.durationMs);
    const event: TelemetryEvent = {
      id: `tel_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      questionId: params.questionId,
      domain: params.domain,
      topic: params.topic,
      durationMs: params.durationMs,
      correct: params.correct,
      sessionElapsedSeconds: params.sessionElapsedSeconds,
      timestamp: params.timestamp ?? Date.now(),
      isImpulsive,
    };

    const data = this.load();
    data.events.push(event);
    this.save(data);
    return event;
  }

  /**
   * Aggregate events across minutes 1–10, 11–20, and 21–30
   * to evaluate cognitive fatigue patterns (FR-DASH-003).
   */
  static analyzeFatigue(customEvents?: TelemetryEvent[]): CognitiveFatigueAnalysis {
    const events = customEvents ?? this.load().events;

    const segDefs = [
      { label: 'Mins 1–10 (Prime Focus)', min: 1, max: 10 },
      { label: 'Mins 11–20 (Sustained Focus)', min: 11, max: 20 },
      { label: 'Mins 21–30 (Fatigue Zone)', min: 21, max: 30 },
    ];

    const buckets: Array<{
      attempts: TelemetryEvent[];
    }> = [{ attempts: [] }, { attempts: [] }, { attempts: [] }];

    for (const ev of events) {
      const idx = getSegmentIndexForSeconds(ev.sessionElapsedSeconds);
      buckets[idx]!.attempts.push(ev);
    }

    const segments = segDefs.map((def, idx) => {
      const items = buckets[idx]!.attempts;
      const totalAttempts = items.length;
      const correctCount = items.filter((i) => i.correct).length;
      const impulsiveCount = items.filter((i) => i.isImpulsive).length;
      const totalDuration = items.reduce((acc, curr) => acc + curr.durationMs, 0);

      const accuracyPercent = totalAttempts > 0 ? Math.round((correctCount / totalAttempts) * 100) : 0;
      const impulsivePercent = totalAttempts > 0 ? Math.round((impulsiveCount / totalAttempts) * 100) : 0;
      const averageDurationMs = totalAttempts > 0 ? Math.round(totalDuration / totalAttempts) : 0;

      return {
        label: def.label,
        minMinute: def.min,
        maxMinute: def.max,
        totalAttempts,
        correctCount,
        accuracyPercent,
        impulsiveCount,
        impulsivePercent,
        averageDurationMs,
      };
    }) as [FatigueSegment, FatigueSegment, FatigueSegment];

    const totalEvents = events.length;
    const totalImpulsive = events.filter((e) => e.isImpulsive).length;
    const overallImpulsivePercent = totalEvents > 0 ? Math.round((totalImpulsive / totalEvents) * 100) : 0;

    // Detect fatigue drop between early (1-10) and late (21-30) segments
    const earlyAccuracy = segments[0].accuracyPercent;
    const lateAccuracy = segments[2].accuracyPercent;
    const accuracyDropPercent = (segments[0].totalAttempts > 0 && segments[2].totalAttempts > 0)
      ? Math.max(0, earlyAccuracy - lateAccuracy)
      : 0;

    const impulsiveSurge = (segments[2].impulsivePercent - segments[0].impulsivePercent) >= 15;
    const fatigueOnsetDetected = accuracyDropPercent >= 15 || impulsiveSurge;

    let recommendedMaxMinutes = 30;
    let parentInsight = 'Tudor maintains strong attention and consistent pacing throughout the full 30 minutes.';

    if (fatigueOnsetDetected) {
      recommendedMaxMinutes = 15;
      parentInsight = `Tudor's accuracy drops by ${accuracyDropPercent}% in minutes 21–30 while impulsive guessing rises to ${segments[2].impulsivePercent}%. We recommend capping sessions at 15–20 minutes with a guided 30-second brain break.`;
    } else if (segments[1].totalAttempts > 0 && segments[1].accuracyPercent < earlyAccuracy - 10) {
      recommendedMaxMinutes = 20;
      parentInsight = 'Mild attention dip observed around minute 15. Inserting a tactile geometry exercise or brain break keeps energy high.';
    }

    return {
      segments,
      totalEvents,
      totalImpulsive,
      overallImpulsivePercent,
      fatigueOnsetDetected,
      accuracyDropPercent,
      recommendedMaxMinutes,
      parentInsight,
    };
  }

  /**
   * Export all user practice, telemetry, and session data to JSON (FR-DASH-004)
   */
  static exportFullArchiveJson(): string {
    const archive = {
      exportDate: new Date().toISOString(),
      student: 'Tudor',
      cohort: 'Jan 2018 (Year 4)',
      telemetry: this.load().events,
      localStorageDump: {
        sprint: localStorage.getItem('clawey_sprint_progress_v1'),
        comprehension: localStorage.getItem('clawey_comprehension_progress_v1'),
        adaptive: localStorage.getItem('clawey_adaptive_mastery_v1'),
        srs: localStorage.getItem('clawey_srs_state_v1'),
        sessionGuard: localStorage.getItem('clawey_session_guard_v1'),
      },
    };

    return JSON.stringify(archive, null, 2);
  }

  /** Trigger browser download of exported JSON file */
  static triggerJsonDownload(): void {
    const jsonStr = this.exportFullArchiveJson();
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const dateStr = new Date().toISOString().split('T')[0];
    a.href = url;
    a.download = `clawey_tudor_analytics_${dateStr}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}
